/**
 * Rechenregeln der fünf Kennzahlen-Kacheln auf Onboarding-Erfolg („Wie gelingt der Einstieg?“).
 *
 * Bis zum 14.10.2026 rechnete die Seite das in `calcKpis` selbst und fiel bei leerem Nenner auf 0
 * zurück. In einem Zeitraum, in dem niemand eingestellt wurde und niemand seine Probezeit beendet hat,
 * stand deshalb groß „0 %“ neben dem kleinen „0 von 0“, und eine Führungskraft liest „Probezeit
 * bestanden: 0 %“ als Misserfolg. Entscheidung vom 14.10.2026: Ohne Person in der Stichprobe gibt es
 * keinen Wert (`null`), und die Kachel zeigt den Strich der Probezeit-Anzeige (`KEIN_WERT`). Der Strich
 * steht in der Kachel (`components/ui/KpiCard.tsx`); diese Regeln liefern eine Zahl oder `null`.
 *
 * Eine echte 0 bleibt eine 0: „0 von 5“ (niemand hat bestanden) ist eine Aussage, „0 von 0“ ist keine.
 * Auch „Neue Mitarbeiter“ ist keine Quote, sondern eine Anzahl. Wurde niemand eingestellt, ist das eine 0
 * und kein fehlender Wert. Diese Kachel geht deshalb an den Regeln vorbei.
 *
 * Bewusst ohne React und ohne Importe, damit die Regeln ohne Oberfläche testbar sind: Das Release Gate
 * führt die Unit-Tests aus, bevor es die UI-Pakete installiert (dieselbe Trennung wie
 * `lib/team-kennzahlen.ts`).
 */

/**
 * Anteil in ganzen Prozent, kaufmännisch gerundet: „3 von 4“ ergibt 75. Ohne Person in der Stichprobe
 * (Nenner 0) gibt es keinen Anteil: `null`.
 *
 * Das ist nicht `quoteProzent` aus `probezeit-anzeige.ts`. Dort wird abgerundet, weil die Ampel der
 * Probezeit an der rohen Quote hängt und die Zahl ihr nicht widersprechen darf. Diese Kacheln haben
 * keine Ampel und runden, wie die Seite es immer getan hat. Auch die Rechenreihenfolge ist die der Seite
 * (erst teilen, dann mal 100): Erst mal 100 und dann teilen ergäbe in seltenen Fällen eine andere Zahl
 * (29 von 200: 14 statt 15).
 */
export function anteilProzent(zaehler: number, nenner: number): number | null {
  return nenner > 0 ? Math.round((zaehler / nenner) * 100) : null;
}

/**
 * Mittelwert, auf ganze Zahlen gerundet. Ohne Wert in der Liste gibt es keinen Mittelwert: `null`. Das
 * gilt für „Ø aus 0 Austritten“; eine Liste, die nur Nullen enthält, ergibt dagegen 0.
 */
export function mittelwertGerundet(werte: readonly number[]): number | null {
  return werte.length > 0 ? Math.round(werte.reduce((a, b) => a + b, 0) / werte.length) : null;
}

/**
 * Veränderung gegenüber dem Vergleichszeitraum für die Pfeilzeile der Kachel: aktueller Wert minus
 * Vergleichswert. Fehlt eine Seite, gibt es keinen Trend, und die Regel liefert 0; die Kachel zeigt dann
 * dasselbe wie bei ausgeschaltetem Vergleich. Fehlen kann der Vergleichswert, weil der Vergleich
 * ausgeschaltet ist (`undefined`) oder weil im Vergleichszeitraum niemand in der Stichprobe war
 * (`null`); der aktuelle Wert, weil es im gewählten Zeitraum niemanden gab.
 *
 * Ohne diese Regel rechnet JavaScript falsch weiter statt zu scheitern: `75 - null` ist 75, ein Plus
 * von 75 Punkten gegen nichts, und `75 - undefined` ist NaN.
 */
export function trendDifferenz(
  aktuell: number | null,
  vergleich: number | null | undefined,
): number {
  return aktuell === null || vergleich === null || vergleich === undefined
    ? 0
    : aktuell - vergleich;
}
